import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon, GlobeAltIcon, KeyIcon, PlusIcon, TrashIcon, ArrowPathIcon, CheckCircleIcon,
  ExclamationTriangleIcon, ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { getApiErrorMessage } from '../../constants/messages';
import {
  IdentityProviderConfig, LoginMethod, LoginPolicy, MfaPolicy, OrgDomain, ScimSyncStatus, ScimTokenResponse,
} from '../../types/api';

interface Props {
  organisationId: string;
  isOwner: boolean;
}

const ALL_METHODS: LoginMethod[] = ['password', 'google', 'entra'];

// Trois cartes : fournisseur Entra ID (tenant), domaines verifies (preuve DNS),
// politique de connexion (methodes acceptees, second facteur). OWNER/ADMIN gerent
// le fournisseur et les domaines ; l'OWNER seul touche la politique, parce que
// c'est elle qui peut enfermer dehors.
const OrganisationSsoSettings: React.FC<Props> = ({ organisationId, isOwner }) => {
  const { t } = (useTranslation as any)('organisations');

  const [provider, setProvider] = useState<IdentityProviderConfig | null>(null);
  const [providerForm, setProviderForm] = useState({ tenant_id: '', display_name: '', is_enabled: true });
  const [providerSaving, setProviderSaving] = useState(false);
  const [domains, setDomains] = useState<OrgDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainBusy, setDomainBusy] = useState<string | null>(null);
  const [policy, setPolicy] = useState<LoginPolicy | null>(null);
  const [policyForm, setPolicyForm] = useState<{ methods: LoginMethod[]; mfa: MfaPolicy }>({ methods: ALL_METHODS, mfa: 'local' });
  const [policySaving, setPolicySaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scim, setScim] = useState<ScimSyncStatus | null>(null);
  const [scimToken, setScimToken] = useState<ScimTokenResponse | null>(null);   // montre une fois, jamais rechargeable
  const [scimBusy, setScimBusy] = useState(false);

  const loadScim = useCallback(async () => {
    try {
      setScim(await apiService.getScimSyncStatus(organisationId));
    } catch {
      setScim(null);
    }
  }, [organisationId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, d, pol] = await Promise.all([
        apiService.getIdentityProvider(organisationId).catch((e: unknown) => {
          const status = (e as { response?: { status?: number } })?.response?.status;
          if (status === 404) return null;
          throw e;
        }),
        apiService.listOrgDomains(organisationId),
        apiService.getLoginPolicy(organisationId),
      ]);
      setProvider(p);
      setProviderForm({ tenant_id: p?.tenant_id ?? '', display_name: p?.display_name ?? '', is_enabled: p?.is_enabled ?? true });
      setDomains(d);
      setPolicy(pol);
      setPolicyForm({ methods: pol.methodes_connexion_autorisees as LoginMethod[], mfa: pol.mfa_policy as MfaPolicy });
      if (p) await loadScim();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('settings.sso.loadError')));
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => { load(); }, [load]);

  // ---------------------------------------------------------------- fournisseur

  const saveProvider = async (e: React.FormEvent) => {
    e.preventDefault();
    setProviderSaving(true);
    try {
      const saved = await apiService.upsertIdentityProvider(organisationId, {
        tenant_id: providerForm.tenant_id.trim(),
        display_name: providerForm.display_name.trim() || null,
        is_enabled: providerForm.is_enabled,
      });
      setProvider(saved);
      toast.success(t('settings.sso.provider.saved'));
      setPolicy(await apiService.getLoginPolicy(organisationId));
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const known = ['entra_tenant_unknown', 'entra_tenant_already_claimed', 'entra_unavailable'];
      toast.error(known.includes(detail || '') ? t(`settings.sso.provider.errors.${detail}`) : getApiErrorMessage(error, t('settings.sso.provider.saveError')));
    } finally {
      setProviderSaving(false);
    }
  };

  const removeProvider = async () => {
    if (!window.confirm(t('settings.sso.provider.confirmRemove') as string)) return;
    setProviderSaving(true);
    try {
      await apiService.deleteIdentityProvider(organisationId);
      setProvider(null);
      setProviderForm({ tenant_id: '', display_name: '', is_enabled: true });
      toast.success(t('settings.sso.provider.removed'));
      setPolicy(await apiService.getLoginPolicy(organisationId));
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail === 'sso_required_by_policy' ? t('settings.sso.provider.errors.sso_required_by_policy') : getApiErrorMessage(error, t('settings.sso.provider.removeError')));
    } finally {
      setProviderSaving(false);
    }
  };

  // ---------------------------------------------------------------- domaines

  const addDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    setDomainBusy('new');
    try {
      const row = await apiService.addOrgDomain(organisationId, domain);
      setDomains(prev => (prev.some(d => d.id === row.id) ? prev : [...prev, row]));
      setNewDomain('');
      toast.success(t('settings.sso.domains.added'));
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const known = ['public_email_domain', 'domain_verified_elsewhere'];
      toast.error(known.includes(detail || '') ? t(`settings.sso.domains.errors.${detail}`) : getApiErrorMessage(error, t('settings.sso.domains.addError')));
    } finally {
      setDomainBusy(null);
    }
  };

  const verifyDomain = async (row: OrgDomain) => {
    setDomainBusy(row.id);
    try {
      const res = await apiService.verifyOrgDomain(organisationId, row.id);
      setDomains(prev => prev.map(d => (d.id === row.id ? res.domain : d)));
      if (res.found) {
        toast.success(t('settings.sso.domains.verified', { domain: row.domain }));
        setPolicy(await apiService.getLoginPolicy(organisationId));
      } else {
        toast.error(t(`settings.sso.domains.check.${res.detail || 'dns_no_txt'}`, { defaultValue: t('settings.sso.domains.check.dns_no_txt') }));
      }
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail === 'domain_verified_elsewhere' ? t('settings.sso.domains.errors.domain_verified_elsewhere') : getApiErrorMessage(error, t('settings.sso.domains.verifyError')));
    } finally {
      setDomainBusy(null);
    }
  };

  const removeDomain = async (row: OrgDomain) => {
    if (!window.confirm(t('settings.sso.domains.confirmRemove', { domain: row.domain }) as string)) return;
    setDomainBusy(row.id);
    try {
      await apiService.deleteOrgDomain(organisationId, row.id);
      setDomains(prev => prev.filter(d => d.id !== row.id));
      toast.success(t('settings.sso.domains.removed'));
      setPolicy(await apiService.getLoginPolicy(organisationId));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('settings.sso.domains.removeError')));
    } finally {
      setDomainBusy(null);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('settings.sso.domains.copied'));
    } catch {
      toast.error(t('settings.sso.domains.copyError'));
    }
  };

  // ---------------------------------------------------------------- politique

  const toggleMethod = (m: LoginMethod) => {
    setPolicyForm(prev => ({
      ...prev,
      methods: prev.methods.includes(m) ? prev.methods.filter(x => x !== m) : [...prev.methods, m],
    }));
  };

  const savePolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (policyForm.methods.length === 0) {
      toast.error(t('settings.sso.policy.atLeastOne'));
      return;
    }
    setPolicySaving(true);
    try {
      const saved = await apiService.updateLoginPolicy(organisationId, {
        methodes_connexion_autorisees: policyForm.methods,
        mfa_policy: policyForm.mfa,
      });
      setPolicy(saved);
      toast.success(t('settings.sso.policy.saved'));
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const known = ['entra_provider_required', 'owner_would_be_locked_out'];
      toast.error(known.includes(detail || '') ? t(`settings.sso.policy.errors.${detail}`) : getApiErrorMessage(error, t('settings.sso.policy.saveError')));
    } finally {
      setPolicySaving(false);
    }
  };

  // ---------------------------------------------------------------- SCIM

  const issueToken = async () => {
    if (scim?.token_prefix && !window.confirm(t('settings.sso.scim.confirmReplace') as string)) return;
    setScimBusy(true);
    try {
      const tok = await apiService.issueScimToken(organisationId);
      setScimToken(tok);
      await loadScim();
      toast.success(t('settings.sso.scim.issued'));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('settings.sso.scim.issueError')));
    } finally {
      setScimBusy(false);
    }
  };

  const revokeToken = async () => {
    if (!window.confirm(t('settings.sso.scim.confirmRevoke') as string)) return;
    setScimBusy(true);
    try {
      await apiService.revokeScimToken(organisationId);
      setScimToken(null);
      await loadScim();
      toast.success(t('settings.sso.scim.revoked'));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('settings.sso.scim.revokeError')));
    } finally {
      setScimBusy(false);
    }
  };

  const outcomeCls = (outcome: string) => {
    if (outcome === 'error' || outcome === 'unauthorized') return 'bg-red-500/12 text-red-400';
    if (outcome === 'owner_protected') return 'bg-yellow-500/12 text-yellow-400';
    if (outcome === 'deactivated' || outcome === 'deleted') return 'bg-surface-3 text-text-secondary';
    return 'bg-green-500/12 text-green-400';
  };

  const inputCls = 'mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500 disabled:opacity-50';
  const primaryBtn = 'inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50';
  const secondaryBtn = 'inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-border-1 bg-surface-3 text-text-secondary hover:bg-surface-2 disabled:opacity-50';

  if (loading) {
    return (
      <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6 flex items-center space-x-3">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent-500" />
        <span className="text-sm text-text-tertiary">{t('settings.sso.loading')}</span>
      </div>
    );
  }

  const verifiedDomains = domains.filter(d => d.is_verified);

  return (
    <>
      {/* ---------------- Fournisseur Entra ID */}
      <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium text-text-primary flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2 text-text-quaternary" />
              {t('settings.sso.provider.title')}
            </h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('settings.sso.provider.description')}</p>
          </div>
          {provider && (
            <span className={`text-xs px-2 py-1 rounded-full ${provider.is_enabled ? 'bg-green-500/12 text-green-400' : 'bg-surface-3 text-text-tertiary'}`}>
              {provider.is_enabled ? t('settings.sso.provider.enabled') : t('settings.sso.provider.disabled')}
            </span>
          )}
        </div>

        {provider && !provider.platform_login_enabled && (
          <div className="mt-4 p-3 rounded-md bg-yellow-500/10 border border-yellow-500/20 text-sm text-yellow-300 flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
            {t('settings.sso.provider.platformDisabled')}
          </div>
        )}

        <form onSubmit={saveProvider} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sso-tenant" className="block text-sm font-medium text-text-secondary">{t('settings.sso.provider.tenantLabel')}</label>
              <input
                id="sso-tenant"
                type="text"
                value={providerForm.tenant_id}
                onChange={(e) => setProviderForm({ ...providerForm, tenant_id: e.target.value })}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                pattern="[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
                className={`${inputCls} font-mono`}
                required
              />
              <p className="mt-1 text-xs text-text-tertiary">{t('settings.sso.provider.tenantHint')}</p>
            </div>
            <div>
              <label htmlFor="sso-name" className="block text-sm font-medium text-text-secondary">{t('settings.sso.provider.nameLabel')}</label>
              <input
                id="sso-name"
                type="text"
                value={providerForm.display_name}
                maxLength={100}
                onChange={(e) => setProviderForm({ ...providerForm, display_name: e.target.value })}
                placeholder={t('settings.sso.provider.namePlaceholder') as string}
                className={inputCls}
              />
            </div>
          </div>
          <label className="flex items-center text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={providerForm.is_enabled}
              onChange={(e) => setProviderForm({ ...providerForm, is_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-border-1 text-accent-600 focus:ring-accent-500 mr-2"
            />
            {t('settings.sso.provider.enabledLabel')}
          </label>

          {provider && (
            <div className="p-3 rounded-md bg-surface-3 text-xs text-text-tertiary">
              <p className="font-medium text-text-secondary mb-1">{t('settings.sso.provider.redirectUriLabel')}</p>
              <div className="flex items-center space-x-2">
                <code className="text-text-primary break-all">{provider.redirect_uri}</code>
                <button type="button" onClick={() => copy(provider.redirect_uri)} className="text-accent-400 hover:text-accent-300" title={t('settings.sso.domains.copy') as string}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2">{t('settings.sso.provider.redirectUriHint')}</p>
            </div>
          )}

          <div className="flex space-x-3">
            <button type="submit" disabled={providerSaving} className={primaryBtn}>
              {providerSaving ? t('settings.sso.saving') : (provider ? t('settings.sso.provider.update') : t('settings.sso.provider.save'))}
            </button>
            {provider && isOwner && (
              <button type="button" onClick={removeProvider} disabled={providerSaving} className={`${secondaryBtn} text-red-400 border-red-500/30 hover:bg-red-500/10`}>
                <TrashIcon className="h-4 w-4 mr-1" />
                {t('settings.sso.provider.remove')}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ---------------- Domaines verifies */}
      <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
        <h2 className="text-lg font-medium text-text-primary flex items-center">
          <GlobeAltIcon className="h-5 w-5 mr-2 text-text-quaternary" />
          {t('settings.sso.domains.title')}
        </h2>
        <p className="mt-1 text-sm text-text-tertiary">{t('settings.sso.domains.description')}</p>

        <form onSubmit={addDomain} className="mt-4 flex space-x-2">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder={t('settings.sso.domains.placeholder') as string}
            className={`${inputCls} mt-0 flex-1`}
            required
          />
          <button type="submit" disabled={domainBusy === 'new'} className={primaryBtn}>
            <PlusIcon className="h-4 w-4 mr-1" />
            {t('settings.sso.domains.add')}
          </button>
        </form>

        <div className="mt-4 divide-y divide-border-1 border border-border-1 rounded-md">
          {domains.map((d) => (
            <div key={d.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 min-w-0">
                  {d.is_verified
                    ? <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                    : <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />}
                  <span className="text-sm font-medium text-text-primary truncate">{d.domain}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${d.is_verified ? 'bg-green-500/12 text-green-400' : 'bg-yellow-500/12 text-yellow-400'}`}>
                    {d.is_verified ? t('settings.sso.domains.statusVerified') : t('settings.sso.domains.statusPending')}
                  </span>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                  <button type="button" onClick={() => verifyDomain(d)} disabled={domainBusy === d.id} className={secondaryBtn}>
                    <ArrowPathIcon className={`h-4 w-4 mr-1 ${domainBusy === d.id ? 'animate-spin' : ''}`} />
                    {d.is_verified ? t('settings.sso.domains.recheck') : t('settings.sso.domains.verify')}
                  </button>
                  <button type="button" onClick={() => removeDomain(d)} disabled={domainBusy === d.id} className="text-red-400 hover:text-red-300 disabled:opacity-50" title={t('settings.sso.domains.remove') as string}>
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {!d.is_verified && (
                <div className="mt-3 p-3 rounded-md bg-surface-3 text-xs text-text-tertiary">
                  <p className="font-medium text-text-secondary mb-2">{t('settings.sso.domains.instructions')}</p>
                  <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
                    <dt>{t('settings.sso.domains.recordType')}</dt><dd><code className="text-text-primary">TXT</code></dd>
                    <dt>{t('settings.sso.domains.recordName')}</dt><dd><code className="text-text-primary">{d.txt_record_name}</code> <span>({t('settings.sso.domains.apex')})</span></dd>
                    <dt>{t('settings.sso.domains.recordValue')}</dt>
                    <dd className="flex items-center space-x-2">
                      <code className="text-text-primary break-all">{d.txt_record_value}</code>
                      <button type="button" onClick={() => copy(d.txt_record_value)} className="text-accent-400 hover:text-accent-300" title={t('settings.sso.domains.copy') as string}>
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    </dd>
                  </dl>
                  <p className="mt-2">{t('settings.sso.domains.propagation')}</p>
                </div>
              )}
              {d.is_verified && d.last_checked_at && (
                <p className="mt-2 text-xs text-text-quaternary">
                  {t('settings.sso.domains.lastChecked', { date: new Date(d.last_checked_at).toLocaleString() })}
                  {d.check_failures > 0 && ` · ${t('settings.sso.domains.failures', { count: d.check_failures })}`}
                </p>
              )}
            </div>
          ))}
          {domains.length === 0 && (
            <p className="p-6 text-center text-sm text-text-tertiary">{t('settings.sso.domains.empty')}</p>
          )}
        </div>
      </div>

      {/* ---------------- Approvisionnement SCIM */}
      {provider && (
        <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-medium text-text-primary flex items-center">
                <ArrowPathIcon className="h-5 w-5 mr-2 text-text-quaternary" />
                {t('settings.sso.scim.title')}
              </h2>
              <p className="mt-1 text-sm text-text-tertiary">{t('settings.sso.scim.description')}</p>
            </div>
            {scim && (
              <span className={`text-xs px-2 py-1 rounded-full ${scim.enabled ? 'bg-green-500/12 text-green-400' : 'bg-surface-3 text-text-tertiary'}`}>
                {scim.enabled ? t('settings.sso.scim.enabled') : t('settings.sso.scim.disabled')}
              </span>
            )}
          </div>

          <div className="mt-4 p-3 rounded-md bg-surface-3 text-xs text-text-tertiary">
            <p className="font-medium text-text-secondary mb-1">{t('settings.sso.scim.endpointLabel')}</p>
            <div className="flex items-center space-x-2">
              <code className="text-text-primary break-all">{scim?.scim_base_url ?? ''}</code>
              {scim && (
                <button type="button" onClick={() => copy(scim.scim_base_url)} className="text-accent-400 hover:text-accent-300" title={t('settings.sso.domains.copy') as string}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-2">{t('settings.sso.scim.endpointHint')}</p>
          </div>

          {scimToken && (
            <div className="mt-4 p-4 rounded-md bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-sm font-medium text-yellow-300 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                {t('settings.sso.scim.revealTitle')}
              </p>
              <p className="mt-1 text-xs text-yellow-200/80">{t('settings.sso.scim.revealHint')}</p>
              <div className="mt-3 flex items-center space-x-2">
                <code className="flex-1 text-sm text-text-primary bg-surface-2 rounded-md px-3 py-2 break-all">{scimToken.token}</code>
                <button type="button" onClick={() => copy(scimToken.token)} className={secondaryBtn}>
                  <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                  {t('settings.sso.domains.copy')}
                </button>
              </div>
              <button type="button" onClick={() => setScimToken(null)} className="mt-3 text-xs text-text-tertiary hover:text-text-secondary">
                {t('settings.sso.scim.revealDone')}
              </button>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" onClick={issueToken} disabled={scimBusy} className={primaryBtn}>
              <KeyIcon className="h-4 w-4 mr-1" />
              {scim?.token_prefix ? t('settings.sso.scim.regenerate') : t('settings.sso.scim.generate')}
            </button>
            {scim?.token_prefix && (
              <>
                <span className="text-xs text-text-tertiary">
                  {t('settings.sso.scim.currentToken', { prefix: scim.token_prefix, date: scim.token_created_at ? new Date(scim.token_created_at).toLocaleDateString() : '' })}
                </span>
                <button type="button" onClick={revokeToken} disabled={scimBusy} className={`${secondaryBtn} text-red-400 border-red-500/30 hover:bg-red-500/10`}>
                  <TrashIcon className="h-4 w-4 mr-1" />
                  {t('settings.sso.scim.revoke')}
                </button>
              </>
            )}
          </div>

          {scim && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-text-primary">{t('settings.sso.scim.statusTitle')}</h3>
                <button type="button" onClick={loadScim} className="text-xs text-accent-400 hover:text-accent-300 flex items-center">
                  <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
                  {t('settings.sso.scim.refresh')}
                </button>
              </div>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-3 rounded-md bg-surface-3">
                  <dt className="text-xs text-text-tertiary">{t('settings.sso.scim.lastSeen')}</dt>
                  <dd className="text-text-primary">{scim.last_seen_at ? new Date(scim.last_seen_at).toLocaleString() : t('settings.sso.scim.never')}</dd>
                </div>
                <div className="p-3 rounded-md bg-surface-3">
                  <dt className="text-xs text-text-tertiary">{t('settings.sso.scim.provisionedUsers')}</dt>
                  <dd className="text-text-primary">{scim.provisioned_users}</dd>
                </div>
                <div className="p-3 rounded-md bg-surface-3">
                  <dt className="text-xs text-text-tertiary">{t('settings.sso.scim.syncedGroups')}</dt>
                  <dd className="text-text-primary">{scim.synced_groups}</dd>
                </div>
                <div className="p-3 rounded-md bg-surface-3">
                  <dt className="text-xs text-text-tertiary">{t('settings.sso.scim.last24h')}</dt>
                  <dd className="text-text-primary">
                    {scim.last_24h.total}
                    {scim.last_24h.errors > 0 && <span className="ml-2 text-red-400">{t('settings.sso.scim.errorsCount', { count: scim.last_24h.errors })}</span>}
                    {scim.last_24h.unauthorized > 0 && <span className="ml-2 text-red-400">{t('settings.sso.scim.unauthorizedCount', { count: scim.last_24h.unauthorized })}</span>}
                  </dd>
                </div>
              </dl>

              <div className="mt-4 overflow-x-auto border border-border-1 rounded-md">
                <table className="min-w-full text-xs">
                  <thead className="bg-surface-3 text-text-tertiary">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t('settings.sso.scim.events.time')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('settings.sso.scim.events.request')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('settings.sso.scim.events.outcome')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('settings.sso.scim.events.detail')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-1">
                    {scim.recent_events.map((e) => (
                      <tr key={e.id} className="hover:bg-surface-3">
                        <td className="px-3 py-2 whitespace-nowrap text-text-tertiary">{new Date(e.received_at).toLocaleString()}</td>
                        <td className="px-3 py-2 font-mono text-text-secondary">
                          {e.method} {e.path.replace(/^\/scim\/v2/, '')}
                          {e.external_id && <span className="ml-2 text-text-quaternary">{e.external_id}</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full ${outcomeCls(e.outcome)}`}>{t(`settings.sso.scim.outcomes.${e.outcome}`, { defaultValue: e.outcome })}</span>
                          <span className="ml-2 text-text-quaternary">{e.status_code}</span>
                        </td>
                        <td className="px-3 py-2 text-text-tertiary">{e.detail || ''}</td>
                      </tr>
                    ))}
                    {scim.recent_events.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-text-tertiary">{t('settings.sso.scim.events.empty')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------------- Politique de connexion */}
      <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
        <h2 className="text-lg font-medium text-text-primary flex items-center">
          <KeyIcon className="h-5 w-5 mr-2 text-text-quaternary" />
          {t('settings.sso.policy.title')}
        </h2>
        <p className="mt-1 text-sm text-text-tertiary">
          {verifiedDomains.length
            ? t('settings.sso.policy.description', { domains: verifiedDomains.map(d => '@' + d.domain).join(', ') })
            : t('settings.sso.policy.descriptionNoDomain')}
        </p>
        {!isOwner && (
          <p className="mt-2 text-xs text-text-tertiary">{t('settings.sso.policy.ownerOnly')}</p>
        )}

        <form onSubmit={savePolicy} className="mt-4 space-y-5">
          <fieldset disabled={!isOwner}>
            <legend className="block text-sm font-medium text-text-secondary">{t('settings.sso.policy.methodsLabel')}</legend>
            <div className="mt-2 space-y-2">
              {ALL_METHODS.map((m) => (
                <label key={m} className="flex items-start text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={policyForm.methods.includes(m)}
                    onChange={() => toggleMethod(m)}
                    disabled={m === 'entra' && !policy?.has_enabled_provider}
                    className="mt-0.5 h-4 w-4 rounded border-border-1 text-accent-600 focus:ring-accent-500 mr-2"
                  />
                  <span>
                    <span className="text-text-primary">{t(`settings.sso.policy.methods.${m}`)}</span>
                    <span className="block text-xs text-text-tertiary">{t(`settings.sso.policy.methodsHint.${m}`)}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset disabled={!isOwner}>
            <legend className="block text-sm font-medium text-text-secondary">{t('settings.sso.policy.mfaLabel')}</legend>
            <div className="mt-2 space-y-2">
              {(['local', 'idp'] as MfaPolicy[]).map((p) => (
                <label key={p} className={`flex items-start p-3 rounded-md border cursor-pointer ${policyForm.mfa === p ? 'border-accent-500 bg-accent-500/8' : 'border-border-1'}`}>
                  <input
                    type="radio"
                    name="mfa-policy"
                    checked={policyForm.mfa === p}
                    onChange={() => setPolicyForm({ ...policyForm, mfa: p })}
                    className="mt-0.5 h-4 w-4 border-border-1 text-accent-600 focus:ring-accent-500"
                  />
                  <span className="ml-3">
                    <span className="text-sm font-medium text-text-primary">{t(`settings.sso.policy.mfa.${p}`)}</span>
                    <span className="block text-xs text-text-tertiary">{t(`settings.sso.policy.mfaHint.${p}`)}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {isOwner && (
            <button type="submit" disabled={policySaving} className={primaryBtn}>
              {policySaving ? t('settings.sso.saving') : t('settings.sso.policy.save')}
            </button>
          )}
        </form>
      </div>
    </>
  );
};

export default OrganisationSsoSettings;
